import { Component, OnInit, OnChanges, Input, OnDestroy } from '@angular/core';
import { AnalyticsStateObject } from '../../models/analytics/analytics-state-object';
import { EditionListService } from '../../editions/edition.service';
import { CurrentUser } from '../../models/current-user';
import { CurrentUserService } from '../services/current-user.service';
import { SubscriptionsService } from '../../subscriptions/subscriptions.service';
import { Router, ActivatedRoute} from '@angular/router';
import { Subscription } from 'rxjs/Subscription';

@Component({
  selector: 'app-breadcrumbs',
  templateUrl: './breadcrumbs.component.html',
  styleUrls: ['./breadcrumbs.component.less']
})
export class BreadcrumbsComponent implements OnInit, OnChanges, OnDestroy {

  @Input() minimalStateObj: AnalyticsStateObject | {categoryType: string; categoryId: number, mapping: string};
  @Input() componentContext: 'analytics' | 'dashboard';
  public breadcrumbs: any;
  public futureLinks: [string, number][] = [];
  public showDropdown: boolean;
  private subs: Subscription[] = [];
  public subscriptionName: string;
  private user: CurrentUser;

  constructor(
    private editionListService: EditionListService,
    private router: Router,
    private currentUserService: CurrentUserService,
    private subscriptionsService: SubscriptionsService
  ) { }

  ngOnInit() {
    this.showDropdown = this.minimalStateObj.mapping !== 'resource';
    this.currentUserService.getUserData().subscribe(user => {
      this.user = user;
      this.setCrumbs(this.minimalStateObj);
    });
  }

  ngOnChanges() {
    this.setCrumbs(this.minimalStateObj);
    this.showDropdown = this.minimalStateObj.mapping !== 'resource';
  }

  ngOnDestroy() {
    this.subs.forEach(sub => sub.unsubscribe());
  }

  private setCrumbs(minimalStateObj: AnalyticsStateObject | {categoryType: string; categoryId: number, mapping: string}) {
    if (minimalStateObj.mapping == 'journey') {
      this.setCrumbsForJourneyMapping(minimalStateObj);
    } else if (minimalStateObj.mapping == 'resource') {
      this.setCrumbsForResourceMapping(minimalStateObj);
    } else if (minimalStateObj.mapping == 'session') {
      this.setCrumbsForSessionMapping(minimalStateObj);
    } else if (minimalStateObj.mapping == 'community') {
      this.setCrumbsForCommunityMapping(minimalStateObj);
    } else if (minimalStateObj.mapping == 'user') {
      this.setCrumbsForUserMapping(minimalStateObj);
    } else if (minimalStateObj.mapping == 'subscription_users') {
      this.setCrumbsForSubscriptionUsersMapping(minimalStateObj);
    } else {
      this.setCrumbsForProgramMapping(minimalStateObj);
    }
  }

  private setCrumbsForSessionMapping(minimalStateObj) {
    this.editionListService.getEditionMapObservable()
      .filter(v => !!v)
      .subscribe(map => {
        let edition = map.getEdition();
        if (minimalStateObj.categoryType === 'edition') {
          this.breadcrumbs = {
            title: "Administration / Programs",
            crumbs: [],
          };
        } else if (minimalStateObj.categoryType === 'session') {
          let session = map.getSession(minimalStateObj.categoryId);
          let program = map.getProgram(session.program.id);
          let wave = program.waves
            .find(w => w._sessionId === session.id);
          let futureLinks = []
          if (wave) {
            futureLinks = wave
              .teams
              .map(team => this.get_team_crumb(team))
          }
          this.breadcrumbs = {
            title: session.name,
            currentCrumb: this.get_session_crumb(session),
            crumbs: [
              this.get_new_programs_crumb(edition),
              this.get_new_program_crumb(program)
            ],
            future: {
              title: 'team',
              links: futureLinks
            }
          };
        } else if (minimalStateObj.categoryType === 'program') {
          let program = map.getProgram(minimalStateObj.categoryId);
          this.breadcrumbs = {
            title: program.name,
            currentCrumb: this.get_new_program_crumb(program),
            crumbs: [this.get_new_programs_crumb(edition)],
            future: {
              title: 'session',
              links: edition.sessions
                .filter(s => s.program.id === program.id)
                .map(session => this.get_session_crumb(session))
            }
          };
        } else if (minimalStateObj.categoryType === 'team') {
          let team = map.getTeam(minimalStateObj.categoryId);
          let session = team.wave.session
          this.breadcrumbs = {
            title: team.name,
            currentCrumb: this.get_team_crumb(session),
            crumbs: [
              this.get_edition_crumb(edition),
              this.get_new_program_crumb(team.program),
              this.get_session_crumb(session)
            ],
            future: {
              title: 'team',
              links: []
            }
          };
        }
        this.futureLinks = this.getFutureLinks(this.breadcrumbs.future);
      }
    )
  }
  private setCrumbsForResourceMapping(minimalStateObj) {
    this.editionListService.getEditionMapObservable()
      .filter(v => !!v)
      .subscribe(map => {
        let edition = map.getEdition();
        if (minimalStateObj.categoryType === 'edition') {
          this.breadcrumbs = {
            title: this.getRootTitle(minimalStateObj),
            crumbs: [],
            future: {
              title: 'resource',
              links: edition.resources.map(resource => this.get_resource_crumb(resource))
            }
          };
        } else if (minimalStateObj.categoryType === 'resource') {
          let resource = map.getResource(minimalStateObj.categoryId) ;
          this.breadcrumbs = {
            title: resource.name,
            currentCrumb: this.get_resource_crumb(resource),
            crumbs: [this.get_edition_crumb(edition)],
            future: null
          };
        }
        this.futureLinks = this.getFutureLinks(this.breadcrumbs.future);
      }
    )
  }

  private setCrumbsForJourneyMapping(minimalStateObj) {
    this.editionListService.getEditionMapObservable()
      .filter(v => !!v)
      .subscribe(map => {
        let edition = map.getEdition();
        if (this.componentContext === 'dashboard') {
          if (minimalStateObj.categoryType === 'edition') {
            this.breadcrumbs = {
              title: 'Administration / Learner Journeys',
              crumbs: [],
            };
          } else if (minimalStateObj.categoryType === 'journey') {
            let journey = map.getJourney(minimalStateObj.categoryId) ;
            this.breadcrumbs = {
              title: journey.name,
              currentCrumb: this.get_journey_crumb(journey),
              crumbs: [this.get_journeys_crumb(edition)],
              future: null
            };
          }
        } else {
          if (minimalStateObj.categoryType === 'edition') {
            this.breadcrumbs = {
              title: this.getRootTitle(minimalStateObj),
              crumbs: [],
              future: {
                title: 'Journey',
                links: edition.journeys.map(journey => this.get_journey_crumb(journey))
              }
            };
          } else if (minimalStateObj.categoryType === 'journey') {
            let journey = map.getJourney(minimalStateObj.categoryId) ;
            let future = null;
            this.breadcrumbs = {
              title: journey.name,
              currentCrumb: this.get_journey_crumb(journey),
              crumbs: [this.get_edition_crumb(edition)],
              future: null
            };
          }
          this.futureLinks = this.getFutureLinks(this.breadcrumbs.future);
        }
      }
    )
  }

  private setCrumbsForUserMapping(minimalStateObj) {
    this.breadcrumbs = {
      title: 'Administration / Users',
      crumbs: []
    };
  }

  private setCrumbsForSubscriptionUsersMapping(minimalStateObj) {
    this.breadcrumbs = {
      title: this.getRootTitle(minimalStateObj),
      crumbs: []
    };
  }

  private setCrumbsForCommunityMapping(minimalStateObj) {
    this.editionListService.getEditionMapObservable()
      .filter(v => !!v)
      .subscribe(map => {
        let edition = map.getEdition();
        if (minimalStateObj.categoryType === 'edition') {
          this.breadcrumbs = {
            title: this.getRootTitle(minimalStateObj),
            crumbs: [],
            future: {
              title: 'Channel',
              links: edition.channels.map(channel => this.get_channel_crumb(channel))
            }
          };
        } else if (minimalStateObj.categoryType === 'channel') {
          let channel = map.getChannel(minimalStateObj.categoryId) ;
          this.breadcrumbs = {
            title: channel.name,
            currentCrumb: this.get_channel_crumb(channel),
            crumbs: [this.get_edition_crumb(edition)],
            future: null
          };
        }
        this.futureLinks = this.getFutureLinks(this.breadcrumbs.future);
      }
    )
  }

  private setCrumbsForProgramMapping(minimalStateObj) {
    this.subs.push(
      this.editionListService.getEditionMapObservable()
        .filter(v => !!v)
        .subscribe(map => {
          let resp;
          if (minimalStateObj.categoryType === 'edition') {
            let edition = map.getEdition();
            resp = {
              title: this.componentContext === 'dashboard' ? 'Administration / Programs' : this.getRootTitle(minimalStateObj),
              crumbs: [],
              future: this.componentContext === 'dashboard' ? null : {
                title: 'program',
                links: edition.programs.map(program => this.get_program_crumb(program))
              }
            };
          } else if (minimalStateObj.categoryType === 'program') {
            let program = map.getProgram(minimalStateObj.categoryId);
            let future = null;
            if (minimalStateObj.subscriptionAdmin) {
              future = null;
            } else if (program.waves.length) {
              future = {
                title: 'wave',
                links: program.waves.map(wave => this.get_wave_crumb(wave))
              };
              if (program.teams.length) {
                program.teams.forEach(team => {
                  future.links.push(this.get_team_crumb(team, 'Other Teams'));
                });
              }
            } else if (program.teams.length) {
              future = {
                title: 'team',
                links: program.teams.map(team => this.get_team_crumb(team))
              };
            }
            resp = {
              title: program.name,
              currentCrumb: this.get_program_crumb(map.getProgram(program.id)),
              crumbs: [this.get_edition_crumb(map.getEdition())],
              future,
            };
          } else if (minimalStateObj.categoryType === 'wave') {
            let wave = map.getWave(minimalStateObj.categoryId);
            resp = {
              title: wave.name,
              currentCrumb: this.get_wave_crumb(map.getWave(wave.id)),
              crumbs: [this.get_edition_crumb(map.getEdition()), this.get_program_crumb(wave.program)],
              future: {
                title: 'team',
                links: wave.teams.map(team => this.get_team_crumb(team))
              }
            };
          } else if (minimalStateObj.categoryType === 'group') {
            let team = map.getTeam(minimalStateObj.categoryId);
            let crumbs = [this.get_edition_crumb(map.getEdition()), this.get_program_crumb(team.program)];
            if (team.wave) {
              crumbs.push(this.get_wave_crumb(team.wave));
            }
            resp = {
              title: team.name,
              currentCrumb: this.get_team_crumb(map.getTeam(team.id)),
              crumbs,
              future: null
            };
          }
          this.breadcrumbs = resp;
          this.futureLinks = this.getFutureLinks(this.breadcrumbs.future);
          // Don't show option to click through to individual teams for team rankings tab
          if (this.componentContext === 'analytics') {
            let stateObj = <AnalyticsStateObject>minimalStateObj;
            if (stateObj.tabType === 'team-rankings' && this.breadcrumbs.future && this.breadcrumbs.future.title === 'team') this.futureLinks = [];
          }
        })
      )
  }

  private getRootTitle(stateObj) {
    if (stateObj.subscriptionId && this.user && !this.user.default_subscription) {
      this.getSubscriptionTitle();
      return "subscription";
    } else {
      return "Global Analytics";
    }
  }

  private getSubscriptionTitle() {
    if (!this.subscriptionName) {
      let subscriptionId = (this.minimalStateObj as AnalyticsStateObject).subscriptionId;
      if (subscriptionId) {
        this.subscriptionsService.getSubscription(subscriptionId).subscribe(sub => {
          this.subscriptionName = sub.name || sub.buyer.name;
        });
      }
    }
  }

  private get_edition_crumb(edition) {
    return {
      title: this.componentContext === 'dashboard' ? 'All Programs' : this.getRootTitle(this.minimalStateObj),
      id: edition.id,
      category: 'edition',
      program_id: null,
    }
  }

  private get_program_crumb(program) {
    return {
      title: program.name,
      id: program.id,
      category: 'program',
      program_id: null,
    }
  }

  private get_new_program_crumb(program) {
    return {
      title: program.name,
      id: program.id,
      category: 'new-program',
      program_id: null,
    }
  }

  private get_new_programs_crumb(edition) {
    return {
      title: 'All Programs',
      id: edition.id,
      category: 'new-programs',
      program_id: null,
    }
  }

  private get_wave_crumb(wave) {
    return {
      title: wave.name,
      id: wave.id,
      category: 'wave',
      program_id: wave.program.id,
    }
  }

  private get_team_crumb(team, section?: string) {
    let resp = {
      title: team.name,
      id: team.id,
      category: 'group',
      program_id: team.program.id,
    }
    if (section) {
      resp['section'] = section;
    }
    return resp;
  }

  private get_journey_crumb(journey) {
    return {
      title: journey.name,
      id: journey.id,
      category: 'journey',
    }
  }

  private get_journeys_crumb(journey) {
    return {
      title: 'Learner Journeys',
      category: 'edition',
      routeArray: ['journeys']
    }
  }

  private get_resource_crumb(resource) {
    return {
      title: resource.name,
      id: resource.id,
      category: 'resource',
    }
  }

  private get_session_crumb(session) {
    return {
      title: session.name,
      id: session.id,
      category: 'session',
    }
  }

  private get_channel_crumb(channel) {
    return {
      title: channel.name,
      id: channel.id,
      category: 'channel',
      routeArray: ['channels']
    }
  }

  private getFutureLinks(future) {
    if (!future || !future.links.length) {
      return [];
    }
    let out = [];
    let section = undefined;
    future.links.forEach((link, index) => {
      if (link.section !== section) {
        out.push(['divider', 'Other Teams']);
        section = link.section;
      }
      out.push([link.title, index]);
    });
    return out;
  }

  private clickCrumb(targetCrumb) {
    let params = {};
    if (targetCrumb.category !== 'edition') {
      params[targetCrumb.category] = targetCrumb.id;
      if (targetCrumb.program_id) {
        params['programId'] = targetCrumb.program_id;
      }
    }
    if (this.componentContext === 'analytics') {
      let stateObj = <AnalyticsStateObject>this.minimalStateObj;
      if (targetCrumb.category === 'channel') {
        this.router.navigate(['analytics', ...stateObj.route, 'channels', targetCrumb.id]);
      }else {
        this.router.navigate(['analytics', ...stateObj.route, params]);
      }
    } else if (targetCrumb.routeArray) {
      this.router.navigate(['dashboard', ...targetCrumb.routeArray]);
    } else if (this.componentContext === 'dashboard') {
      let trunkCrumbIndex = this.breadcrumbs.crumbs.findIndex(
        crumb => (targetCrumb.id === crumb.id && targetCrumb.categoryType === crumb.categoryType)
      );
      let targetCrumbArray = this.breadcrumbs.crumbs.slice(1);
      if (trunkCrumbIndex !== -1) {
        // If the target crumb is a rendered crumb, in the trunk, rather than in a future branch, limit the route array
        targetCrumbArray = targetCrumbArray.slice(0, trunkCrumbIndex);
      } else {
        // If the target crumb is in a future branch, append the current crumb and the target crumb route to full trunk route
        if (this.breadcrumbs.currentCrumb) {
          targetCrumbArray.push(this.breadcrumbs.currentCrumb); 
        };
        targetCrumbArray.push(targetCrumb);
      }
      let targetRouteArray = [];
      if (targetCrumb.category === 'session') { // TO DO - remove these two conditions after uniftying new-programs and programs
        targetRouteArray = [
          'new-programs',
          this.breadcrumbs.currentCrumb.program_id || this.breadcrumbs.currentCrumb.id,
          'sessions',
          targetCrumb.id
        ];
      } else if (targetCrumb.category === 'new-programs'){
        targetRouteArray = ['new-programs'];
      } else {
        // Render out crumb array into an array of url segments
        targetRouteArray = [].concat(...targetCrumbArray.map(crumb => {
          return [(crumb.category === 'group' ? 'team' : crumb.category) + 's', crumb.id.toString()]
        }));
      }
      this.router.navigate(['dashboard', ...targetRouteArray])
    }
    return false;
  }

  private futureLinkSelected(index) {
    let link = this.breadcrumbs['future']['links'][index];
    this.clickCrumb(link);
  }

  public crumbTitle(crumb): string {
    return crumb.title === 'subscription'
      ? `Subscription (${this.subscriptionName || '...'})`
      : crumb.title;
  }
}



// WEBPACK FOOTER //
// /home/runner/work/nomadic-admin/nomadic-admin/nomadic-admin/src/app/shared/breadcrumbs/breadcrumbs.component.ts